import React, { useState, useMemo, useEffect } from "react";
import {
  Box, Container, Typography, Stack, TextField, Grid, Card, CardContent,
  Chip, Avatar, InputAdornment, useTheme, alpha, Paper, Tabs, Tab, Divider
} from "@mui/material";
import {
  Search as SearchIcon,
  ReceiptLong as ReceiptIcon,
  AccessTime as PendingIcon,
  CheckCircle as ConfirmedIcon,
  TaskAlt as CompletedIcon,
  Cancel as CanceledIcon,
  LocalMall as ProductIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getAllOrders } from "../redux/slices/product/thunk";

const BASE_URL = "https://red-90.com";
const joinUrl = (path) => path?.startsWith("http") ? path : `${BASE_URL}${path}`;

export default function MyOrdersPage() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";

  const { myOrders } = useSelector((state) => state.product);
  const orders = myOrders?.data || [];
  const user = JSON.parse(localStorage.getItem('user'));

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    if (user?._id) {
      dispatch(getAllOrders({ id: user._id, query: 'page=1&limit=2000' }));
    }
  }, [dispatch, user?._id]);

  // الحالات المتاحة
  const statusTabs = [
    { value: "all", label: isRtl ? "الكل" : "All", icon: <ReceiptIcon sx={{ fontSize: 20 }} /> },
    { value: "pending", label: isRtl ? "قيد الانتظار" : "Pending", icon: <PendingIcon sx={{ fontSize: 20 }} /> },
    { value: "confirmed", label: isRtl ? "تم التأكيد" : "Confirmed", icon: <ConfirmedIcon sx={{ fontSize: 20 }} /> },
    { value: "completed", label: isRtl ? "مكتمل" : "Completed", icon: <CompletedIcon sx={{ fontSize: 20 }} /> },
    { value: "canceled", label: isRtl ? "ملغي" : "Canceled", icon: <CanceledIcon sx={{ fontSize: 20 }} /> },
  ];

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      const productName = isRtl ? order.product?.title_ar : order.product?.title_en;
      const matchesSearch = productName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesStatus = statusFilter === "all" || order.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [orders, searchQuery, statusFilter, isRtl]);

  const getStatusConfig = (status) => {
    const configs = {
      pending: { color: "#FF9800", icon: <PendingIcon />, label: isRtl ? "قيد الانتظار" : "Pending" },
      confirmed: { color: "#2196F3", icon: <ConfirmedIcon />, label: isRtl ? "تم التأكيد" : "Confirmed" },
      completed: { color: "#4CAF50", icon: <CompletedIcon />, label: isRtl ? "مكتمل" : "Completed" },
      canceled: { color: "#F44336", icon: <CanceledIcon />, label: isRtl ? "ملغي" : "Canceled" },
    };
    return configs[status] || { color: "#757575", icon: <PendingIcon />, label: status };
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: alpha(theme.palette.background.default, 0.95), pb: 12, pt: 6 }}>
      <Container maxWidth="md">

        {/* Page Header */}
        <Box sx={{ mb: 5, textAlign: "center" }}>
          <Typography variant="h3" fontWeight={1000} sx={{ color: theme.palette.primary.main, mb: 1 }}>
            {t("nav.orders")}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ opacity: 0.8 }}>
            {isRtl ? "تتبع مشترياتك وطلباتك السابقة" : "Track your purchases and past orders"}
          </Typography>
        </Box>

        {/* Search & Tabs Section */}
        <Paper elevation={0} sx={{ p: 1, mb: 4, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, bgcolor: "background.paper" }}>
          <TextField
            fullWidth
            placeholder={isRtl ? "ابحث باسم المنتج..." : "Search by product name..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            variant="standard"
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <InputAdornment position="start" sx={{ pl: 2 }}>
                  <SearchIcon color="primary" />
                </InputAdornment>
              ),
            }}
            sx={{
              bgcolor: alpha(theme.palette.divider, 0.05),
              borderRadius: 4,
              p: 1.5,
              mb: 1
            }}
          />

          <Tabs
            value={statusFilter}
            onChange={(e, newValue) => setStatusFilter(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": {
                minHeight: 45,
                borderRadius: 3,
                mx: 0.5,
                fontWeight: 800,
                textTransform: "none",
                transition: "0.2s",
                color: "text.secondary",
                "&.Mui-selected": {
                  bgcolor: theme.palette.primary.main,
                  color: "#fff",
                }
              }
            }}
          >
            {statusTabs.map((tab) => (
              <Tab
                key={tab.value}
                value={tab.value}
                label={tab.label}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Paper>

        {/* Orders List */}
        <Stack spacing={2.5}>
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order) => {
              const config = getStatusConfig(order.status);
              return (
                <Card key={order._id} elevation={0} sx={{
                  borderRadius: 3,
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: "visible",
                  position: "relative",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.1)}`
                  }
                }}>
                  <CardContent sx={{ p: 2.5 }}>
                    <Grid container spacing={2} alignItems="center">
                      {/* Image Container */}
                      <Grid item xs={12} sm={2.5}>
                        <Box sx={{ position: "relative", width: { xs: 120, sm: "100%" }, mx: "auto" }}>
                          <Avatar
                            src={joinUrl(order.product?.image)}
                            variant="rounded"
                            sx={{
                              width: "100%",
                              height: 100,
                              borderRadius: 4,
                              boxShadow: "0 8px 16px rgba(0,0,0,0.1)",
                              bgcolor: "background.default"
                            }}
                          >
                            <ProductIcon />
                          </Avatar>
                        </Box>
                      </Grid>

                      {/* Details Container */}
                      <Grid item xs={12} sm={6.5}>
                        <Stack spacing={0.5} textAlign={{ xs: "center", sm: isRtl ? "right" : "left" }}>
                          <Typography variant="h6" fontWeight={900}>
                            {isRtl ? order.product?.title_ar : order.product?.title_en}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent={{ xs: "center", sm: "flex-start" }}
                            alignItems="center"
                            divider={<Divider orientation="vertical" flexItem sx={{ height: 12, my: "auto" }} />}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {new Date(order.createdAt).toLocaleDateString(i18n.language, { day: 'numeric', month: 'short' })}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {isRtl ? "الكمية:" : "Qty:"} {order.quantity}
                            </Typography>
                            <Typography variant="body2" fontWeight={700} color={order.paid ? "success.main" : "error.main"}>
                              {order.paid ? (isRtl ? "مدفوع" : "Paid") : (isRtl ? "غير مدفوع" : "Unpaid")}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Grid>

                      {/* Price & Status Container */}
                      <Grid item xs={12} sm={3}>
                        <Stack alignItems={{ xs: "center", sm: "flex-end" }} spacing={1}>
                          <Typography variant="h6" fontWeight={1000} color="primary.main">
                            {order.totalPrice} EGP
                          </Typography>
                          <Chip
                            label={config.label}
                            sx={{
                              bgcolor: alpha(config.color, 0.15),
                              color: config.color,
                              fontWeight: 900,
                              borderRadius: 2,
                              border: `1px solid ${alpha(config.color, 0.3)}`,
                              "& .MuiChip-label": { px: 2 }
                            }}
                          />
                        </Stack>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              );
            })
          ) : (
            <Box sx={{ textAlign: "center", mt: 10 }}>
              <Avatar sx={{ width: 80, height: 80, mx: "auto", mb: 2, bgcolor: alpha(theme.palette.divider, 0.1) }}>
                <ReceiptIcon sx={{ fontSize: 40, color: "text.disabled" }} />
              </Avatar>
              <Typography variant="h6" color="text.secondary" fontWeight={800}>
                {isRtl ? "لا توجد طلبات هنا" : "No orders found here"}
              </Typography>
              <Typography variant="body2" color="text.disabled">
                {isRtl ? "جرب تغيير الفلتر أو ابحث عن منتج آخر" : "Try changing the filter or search for another product"}
              </Typography>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
}